/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { ActionSuccess, AnalyticsUtils } from '@wit/vtv-x-foundation-lib';
import { SagaIterator } from 'redux-saga';
import { all, put, select, takeEvery } from 'redux-saga/effects';
import { StoreState } from '../../store';
import { getMainPathName, getSubPathName } from './navigationUtils';
import * as NavigationWatcher from './navigationWatcher';

const MAX_SECTIONS_SIZE = 3;

export enum ActionType {
  RESET = 'analytics/navigationPathWatcher/RESET',
  ADD_SECTION = 'analytics/navigationPathWatcher/ADD',
  REPLACE_SECTION = 'analytics/navigationPathWatcher/REPLACE',
}

export interface NavigationPathSections {
  path: string;
  additionalInfo?: string;
}

type Reset = ActionSuccess<ActionType.RESET>;
type AddSection = ActionSuccess<ActionType.ADD_SECTION, NavigationPathSections>;
type ReplaceSection = ActionSuccess<ActionType.REPLACE_SECTION, NavigationPathSections>;

type Actions = Reset | AddSection | ReplaceSection;

const Actions = {
  reset: (): Reset => ({
    type: ActionType.RESET,
  }),
  addSection: (path: string, additionalInfo?: string): AddSection => ({
    type: ActionType.ADD_SECTION,
    payload: { path, additionalInfo },
  }),
  replaceSection: (path: string, additionalInfo?: string): ReplaceSection => ({
    type: ActionType.REPLACE_SECTION,
    payload: { path, additionalInfo },
  }),
};

type ReducerState = {
  sections: NavigationPathSections[];
};

const initialState: ReducerState = {
  sections: [],
};

export default function currentFolder(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionType.RESET: {
      return initialState;
    }
    case ActionType.ADD_SECTION: {
      if (state.sections.length < MAX_SECTIONS_SIZE) {
        return {
          ...state,
          sections: [...state.sections, action.payload],
        };
      }
      return state;
    }
    case ActionType.REPLACE_SECTION: {
      const newSections = [...state.sections];
      newSections.pop();
      return {
        ...state,
        sections: [...newSections, action.payload],
      };
    }
    default:
      return state;
  }
}

export const Selectors = {
  getSections: (state: StoreState): NavigationPathSections[] => state.analyticsWatchers.navigationPathWatcher.sections,
};

function* watchRouteChange(action: NavigationWatcher.RouteChange): SagaIterator {
  const { path, asset } = action.meta;
  const subSection: string | undefined = getSubPathName(path);
  if (subSection !== undefined) {
    const sections: NavigationPathSections[] = yield select(Selectors.getSections);
    if (sections.findIndex((curr) => curr.path === subSection) >= 0) {
      // if a section with the same path is already added replace it to prevent the path to grow indefinitely
      yield put(Actions.replaceSection(subSection, AnalyticsUtils.getMediaId(asset)));
      return;
    }
    yield put(Actions.addSection(subSection, AnalyticsUtils.getMediaId(asset)));
    return;
  }
  const mainSection: string | null | undefined = getMainPathName(path);
  if (mainSection !== undefined) {
    yield put(Actions.reset());
    if (mainSection !== null) {
      yield put(Actions.addSection(mainSection, AnalyticsUtils.getMediaId(asset)));
    }
  }
}

export function* saga(): SagaIterator {
  yield all([takeEvery(NavigationWatcher.ActionType.ROUTE_CHANGE, watchRouteChange)]);
}
